import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../settings";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Zeke", href: "#why" },
  { label: "Our Work", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (window.__lenis) {
      window.__lenis.scrollTo(href, { offset: -70 });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="flex h-[70px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-brand-link" className="flex items-center gap-3">
          <Logo className="h-14 w-14" />
          <span className="leading-tight">
            <span className="font-heading block text-base font-bold tracking-[0.18em]">{settings.company_name.toUpperCase()}</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50">
              {settings.tagline}
            </span>
          </span>
        </a>

        <nav data-testid="header-nav" className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-xs font-medium uppercase tracking-[0.22em] text-white/60 transition-colors duration-300 hover:text-volt"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#quote"
            onClick={(e) => go(e, "#quote")}
            data-testid="header-quote-button"
            className="bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-volt"
          >
            Get a Quote
          </a>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="p-2 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/95 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="py-3 font-heading text-2xl font-bold uppercase tracking-wide text-white/80 transition-colors hover:text-volt"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#quote"
                onClick={(e) => go(e, "#quote")}
                data-testid="mobile-quote-button"
                className="mt-4 bg-volt px-5 py-3 text-center text-sm font-bold uppercase tracking-[0.2em] text-black"
              >
                Get a Quote
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
